import { ReactNode, useMemo } from "react";
import moment from "moment";
import QRCode from "react-qr-code";
import { useOutletContext } from "react-router-dom";
import { StoreData } from "../../types/StoreData";
import { Button } from "@tides/base-library";
import { BTCPaymentInfo } from "../../types/BTCTypes";
import useBTCPayment from "../../hooks/useBTCPayment";
import { Spinner } from "../Spinner";

interface Props {
  info: BTCPaymentInfo;
  price: string;
}

export default function BTCPayment({ info, price }: Props): ReactNode {
  const { expired, progress, expiration, totalSeconds } = useBTCPayment(
    info,
    info.product_id,
    info.invoice_id
  );
  const { shop_name } = useOutletContext<StoreData>();

  const memoizedQRCode = useMemo(
    () => <QRCode className="self-center" value={info.payment_link} />,
    [info.payment_link]
  );

  return (
    <article className="text-foreground flex flex-col gap-8">
      <article className="flex flex-col gap-2.5">
        <label className="text-primary text-center">
          <b>{shop_name}</b>
        </label>
        <h1 className="text-center text-xl">USD {price}</h1>
      </article>
      {!expired ? (
        <section className="text-center">
          <article className="bg-background relative mb-5 flex w-full flex-col justify-between rounded">
            <section
              className="bg-primary absolute flex h-10 rounded text-center"
              style={{ width: `${progress}%` }}
            ></section>

            <Spinner text="Awaiting payment..." />

            <label className="z-10 mr-4 text-center">
              {moment.utc(expiration!.asMilliseconds()).format("mm:ss")}
            </label>
          </article>

          <section className="flex justify-between rounded border-b pb-5">
            <p>Paying with</p>
            <p className="float-right">{info.type}</p>
          </section>

          <section className="flex justify-between rounded border-b pb-5">
            <p>Amount</p>
            <p className="float-right">{info.amount}</p>
          </section>

          {memoizedQRCode}

          <Button variant="link">
            <a href={info.payment_link} target="_top" className="text-2xl">
              Open in wallet
            </a>
          </Button>
        </section>
      ) : (
        <section className="p-4">
          <article className="relative mb-5 flex w-full justify-between rounded bg-red-500 text-white">
            <label className="z-10 ml-4">Invoice Expired</label>
          </article>

          <article className="flex flex-col gap-10">
            <p className="break-words text-sm font-bold text-zinc-500">
              This invoice has expired. An invoice is only valid for{" "}
              {totalSeconds.humanize(true)}. You can resubmit this form if you
              would like to submit your payment again.
            </p>

            <p className="break-words text-sm font-bold text-zinc-500">
              If you tried to send a payment, it has not yet been accepted by
              the network. We have not yet received your funds.
            </p>

            <p className="break-words text-sm font-bold text-zinc-500">
              If we receive it at a later point, we will either process your
              order or contact you to make refund arrangements...
            </p>

            <article className="flex gap-2.5">
              <b>Order ID:</b>
              <label className="text-zinc-500">{info.invoice_id}</label>
            </article>
          </article>
        </section>
      )}
    </article>
  );
}
